import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaTerminal,
  FaServer,
  FaClock,
  FaShieldAlt,
  FaCopy,
  FaCheck,
  FaDownload,
  FaLightbulb,
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaRedo,
  FaSlidersH,
  FaCheckCircle,
  FaTimesCircle,
  FaCogs
} from 'react-icons/fa';

import {
  LINUX_PRESETS,
  CRON_PRESETS,
  translateCronToHuman,
  getNextExecutionPreviews,
  calculateHardeningScore,
  generateSystemdService,
  generateSystemdTimer,
  generateDeploymentBashScript
} from '../utils/linuxDevOpsData';

const LinuxDevOpsPage = () => {
  // Navigation & View State
  const [activeTab, setActiveTab] = useState('systemd'); // 'systemd', 'cron', 'hardening', 'deploy'
  const [copiedCode, setCopiedCode] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showPrinciples, setShowPrinciples] = useState(false);

  // Active Configuration State (default: Node.js Production API)
  const [config, setConfig] = useState(() => LINUX_PRESETS[0].config);
  const [activePresetId, setActivePresetId] = useState('nodejs_api');

  // Hardening Score calculation
  const hardening = useMemo(() => calculateHardeningScore(config), [config]);

  // Cron plain English & next executions
  const cronHuman = useMemo(
    () => translateCronToHuman(config.cronMinute, config.cronHour, config.cronDom, config.cronMonth, config.cronDow),
    [config.cronMinute, config.cronHour, config.cronDom, config.cronMonth, config.cronDow]
  );

  const nextExecutions = useMemo(
    () => getNextExecutionPreviews(config.cronMinute, config.cronHour),
    [config.cronMinute, config.cronHour]
  );

  // Apply Preset
  const handleApplyPreset = (preset) => {
    setActivePresetId(preset.id);
    setConfig({ ...preset.config });
  };

  // Update field
  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Apply Cron Preset
  const handleApplyCronPreset = (cp) => {
    setConfig(prev => ({
      ...prev,
      cronMinute: cp.minute,
      cronHour: cp.hour,
      cronDom: cp.dom,
      cronMonth: cp.month,
      cronDow: cp.dow,
      cronPreset: cp.id,
      timerCalendar: cp.calendar
    }));
  };

  // Copy code helper
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // Download file helper
  const handleDownload = (filename, content) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'systemd', label: 'Systemd Service Unit Architect', icon: <FaServer /> },
    { id: 'cron', label: 'Visual Cron & Systemd Timer Scheduler', icon: <FaClock /> },
    { id: 'hardening', label: 'CIS Security Hardening & Audit', icon: <FaShieldAlt /> },
    { id: 'deploy', label: 'Automated Deployment Shell Script', icon: <FaTerminal /> }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#040d1a',
      color: '#e2e8f0',
      padding: '30px 20px 80px 20px',
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        
        {/* Breadcrumb Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ff4757', fontSize: '0.88rem', fontWeight: 600 }}>
            <Link to="/tools" style={{ color: '#94a3b8', textDecoration: 'none' }}>Tools Hub</Link>
            <span style={{ color: '#64748b' }}>/</span>
            <span style={{ color: '#ff4757' }}>Linux & DevOps Automation</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.78rem', background: 'rgba(255, 71, 87, 0.15)', color: '#ff4757', border: '1px solid rgba(255, 71, 87, 0.3)', padding: '4px 10px', borderRadius: '6px', fontWeight: 700 }}>
              Red Hat Enterprise Linux (RHEL 9) / systemd 252+
            </span>
          </div>
        </div>

        {/* Hero Banner with Live Daemon Hardening KPI */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(38, 8, 14, 0.85) 0%, rgba(4, 15, 30, 0.95) 100%)',
          border: '1px solid rgba(255, 71, 87, 0.35)',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '26px',
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255, 71, 87, 0.15)', border: '1px solid rgba(255, 71, 87, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#ff4757', fontWeight: 700, marginBottom: '8px' }}>
              <FaTerminal size={13} />
              <span>Red Hat Academy & Production Linux Administration Suite</span>
            </div>
            <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
              Linux & DevOps Automation Suite
            </h1>
            <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '720px', lineHeight: '1.5' }}>
              Architect resilient systemd service daemons, dual Cron & Systemd Timer schedules, CIS benchmark security sandboxing, and automated bash installation scripts.
            </p>

            {/* Guide & Principles Toggles */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowGuide(!showGuide)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: showGuide ? 'rgba(255, 71, 87, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                  border: `1px solid ${showGuide ? '#ff4757' : 'rgba(255, 71, 87, 0.3)'}`,
                  color: showGuide ? '#ff4757' : '#e2e8f0',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <FaBookOpen size={12} />
                <span>{showGuide ? 'Hide User Guide' : 'How to Use Guide'}</span>
                {showGuide ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>

              <button
                onClick={() => setShowPrinciples(!showPrinciples)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: showPrinciples ? 'rgba(248, 210, 16, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                  border: `1px solid ${showPrinciples ? '#f8d210' : 'rgba(248, 210, 16, 0.3)'}`,
                  color: showPrinciples ? '#f8d210' : '#e2e8f0',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <FaLightbulb size={12} color="#f8d210" />
                <span>{showPrinciples ? 'Hide RHEL Principles' : 'Linux Systems Administration Best Practices'}</span>
                {showPrinciples ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>
            </div>
          </div>

          {/* Hardening Status KPI Block */}
          <div style={{
            background: 'rgba(24, 6, 10, 0.9)',
            border: '1px solid rgba(255, 71, 87, 0.4)',
            borderRadius: '12px',
            padding: '18px 24px',
            textAlign: 'right',
            minWidth: '220px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
              Security Hardening Score
            </div>
            <div style={{ fontSize: '2rem', color: hardening.badgeColor, fontWeight: 800, fontFamily: 'monospace', margin: '4px 0' }}>
              {hardening.score}%
            </div>
            <div style={{ fontSize: '0.82rem', color: hardening.badgeColor, fontWeight: 700 }}>
              {hardening.grade}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
              User: <code style={{ color: '#00d2d3' }}>{config.user}</code> | Restart: <strong style={{ color: '#f8fafc' }}>{config.restartPolicy}</strong>
            </div>
          </div>
        </div>

        {/* COLLAPSIBLE HOW TO USE GUIDE */}
        {showGuide && (
          <div style={{
            background: 'rgba(8, 20, 39, 0.85)',
            border: '1px solid rgba(255, 71, 87, 0.3)',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '26px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255, 71, 87, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4757' }}>
                  <FaBookOpen size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                    How to Architect and Deploy Linux Daemons
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                    Follow this 4-step workflow to generate robust systemd units, scheduled timers, security hardening, and deployment scripts.
                  </p>
                </div>
              </div>
              <span style={{ fontSize: '0.75rem', background: 'rgba(255, 71, 87, 0.15)', color: '#ff4757', padding: '3px 10px', borderRadius: '12px', fontWeight: 700 }}>
                SysAdmin Workflow
              </span>
            </div>

            {/* 4 Step Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '14px' }}>
              
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#ff4757', color: '#ffffff', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#ff4757' }}>Choose Archetype Preset</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  Select from 4 production-ready templates below (Node.js API, FastAPI, Postgres Backup, or Go Daemon) to preload verified execution paths and settings.
                </p>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#00d2d3', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#00d2d3' }}>Tune Service & Cgroups</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  In the <strong>Systemd Architect</strong> tab, configure restart policies (<code style={{ color: '#00d2d3' }}>always</code> vs <code style={{ color: '#00d2d3' }}>on-failure</code>), memory limits (<code style={{ color: '#00d2d3' }}>MemoryMax</code>), and sandboxing.
                </p>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#f8d210', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f8d210' }}>Schedule via Cron or Timer</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  On the <strong>Cron & Timer</strong> tab, inspect the plain English schedule translation, view the next 5 execution timestamps, and generate a modern <code style={{ color: '#f8d210' }}>.timer</code> unit.
                </p>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2ecc71', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2ecc71' }}>Export Bash Deploy Script</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  On the <strong>Deployment Script</strong> tab, copy or download the automated bash setup script to provision user accounts, directory permissions, and log rotation.
                </p>
              </div>

            </div>
          </div>
        )}

        {/* COLLAPSIBLE RHEL PRINCIPLES */}
        {showPrinciples && (
          <div style={{
            background: 'rgba(8, 20, 39, 0.85)',
            border: '1px solid rgba(248, 210, 16, 0.3)',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '26px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(248, 210, 16, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f8d210' }}>
                  <FaCogs size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                    Red Hat Enterprise Linux (RHEL) Daemon Design Principles
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                    Production system engineering principles for reliable, secure Linux services.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowPrinciples(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                ✕ Close
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  🛡️ Principle of Least Privilege
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Never execute network daemons as root. Create dedicated system users with <code style={{ color: '#00d2d3' }}>/sbin/nologin</code> shells and ownership restricted strictly to application directories.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  🔒 Filesystem Sandboxing (ProtectSystem)
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Setting <code style={{ color: '#00d2d3' }}>ProtectSystem=strict</code> mounts the OS hierarchy as read-only. Even if an attacker gains code execution, they cannot modify binaries or config files.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  📊 Linux Cgroups Resource Guardrails
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Memory leaks can crash entire production servers. Enforcing <code style={{ color: '#00d2d3' }}>MemoryMax</code> ensures only the rogue process is terminated by the OOM killer without taking down the host.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  ⏱️ Timers over Crontab (Modern Systemd)
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Systemd timers offer centralized logging via <code style={{ color: '#00d2d3' }}>journalctl</code>, dependency management, randomized delays, and execution history (<code style={{ color: '#00d2d3' }}>systemctl list-timers</code>) over traditional cron.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1-Click Production Presets */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaLightbulb color="#f8d210" size={13} />
            <span>Production Daemon Archetype Presets</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            {LINUX_PRESETS.map(preset => {
              const isSelected = activePresetId === preset.id;
              return (
                <div
                  key={preset.id}
                  onClick={() => handleApplyPreset(preset)}
                  style={{
                    backgroundColor: isSelected ? 'rgba(255, 71, 87, 0.12)' : 'rgba(8, 20, 39, 0.65)',
                    border: `1px solid ${isSelected ? '#ff4757' : 'rgba(148, 163, 184, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isSelected ? '#ff4757' : '#f8fafc' }}>
                      {preset.name}
                    </span>
                    <span style={{
                      fontSize: '0.68rem',
                      background: isSelected ? '#ff4757' : 'rgba(148, 163, 184, 0.15)',
                      color: isSelected ? '#ffffff' : '#94a3b8',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {preset.badge}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0, lineHeight: '1.35' }}>
                    {preset.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
          marginBottom: '24px',
          overflowX: 'auto',
          paddingBottom: '2px'
        }}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: isActive ? 'rgba(255, 71, 87, 0.15)' : 'transparent',
                  color: isActive ? '#ff4757' : '#94a3b8',
                  border: 'none',
                  borderBottom: `2px solid ${isActive ? '#ff4757' : 'transparent'}`,
                  padding: '10px 18px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '6px 6px 0 0',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB 1: SYSTEMD SERVICE UNIT ARCHITECT */}
        {activeTab === 'systemd' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            
            {/* Left Column: Form Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Unit & Metadata */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ff4757', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaServer />
                  <span>[Unit] & [Service] Core Settings</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Service Name (without .service)</label>
                    <input
                      type="text"
                      value={config.serviceName}
                      onChange={(e) => updateField('serviceName', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Service Type</label>
                    <select
                      value={config.serviceType}
                      onChange={(e) => updateField('serviceType', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      <option value="simple">simple (Standard Daemon)</option>
                      <option value="forking">forking (Traditional background daemon)</option>
                      <option value="oneshot">oneshot (Short-lived task / backup)</option>
                      <option value="notify">notify (Signals systemd via sd_notify)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Description</label>
                  <input
                    type="text"
                    value={config.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                  />
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>ExecStart Command (Absolute Path)</label>
                  <input
                    type="text"
                    value={config.execStart}
                    onChange={(e) => updateField('execStart', e.target.value)}
                    style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Execution User</label>
                    <input
                      type="text"
                      value={config.user}
                      onChange={(e) => updateField('user', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Working Directory</label>
                    <input
                      type="text"
                      value={config.workingDirectory}
                      onChange={(e) => updateField('workingDirectory', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Restart Policy</label>
                    <select
                      value={config.restartPolicy}
                      onChange={(e) => updateField('restartPolicy', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      <option value="always">always (Auto-restart unconditionally)</option>
                      <option value="on-failure">on-failure (Restart on crash/exit code != 0)</option>
                      <option value="no">no (Do not restart)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Restart Delay (Seconds)</label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={config.restartSec}
                      onChange={(e) => updateField('restartSec', parseInt(e.target.value) || 0)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* Cgroups & Resource Management */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d2d3', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaSlidersH />
                  <span>Linux Cgroups Resource Limits</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>MemoryMax</label>
                    <input
                      type="text"
                      placeholder="e.g. 1024M"
                      value={config.memoryMax}
                      onChange={(e) => updateField('memoryMax', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>CPUQuota</label>
                    <input
                      type="text"
                      placeholder="e.g. 150%"
                      value={config.cpuQuota}
                      onChange={(e) => updateField('cpuQuota', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>LimitNOFILE</label>
                    <input
                      type="number"
                      value={config.limitNoFile}
                      onChange={(e) => updateField('limitNoFile', parseInt(e.target.value) || 1024)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Code Preview & Export */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#f8fafc' }}>
                      /etc/systemd/system/{config.serviceName}.service
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Live Validated Systemd Unit File</div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleCopy(generateSystemdService(config))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: copiedCode ? '#2ecc71' : '#ff4757',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {copiedCode ? <FaCheck /> : <FaCopy />}
                      <span>{copiedCode ? 'Copied!' : 'Copy Unit'}</span>
                    </button>

                    <button
                      onClick={() => handleDownload(`${config.serviceName}.service`, generateSystemdService(config))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: 'rgba(148, 163, 184, 0.15)',
                        color: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload />
                      <span>Download .service</span>
                    </button>
                  </div>
                </div>

                <div style={{
                  background: '#0a192f',
                  border: '1px solid rgba(255, 71, 87, 0.25)',
                  borderRadius: '8px',
                  padding: '16px',
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: '520px'
                }}>
                  <pre style={{ margin: 0, fontFamily: "'Courier New', Courier, monospace", fontSize: '0.82rem', color: '#38ef7d', lineHeight: '1.45' }}>
                    {generateSystemdService(config)}
                  </pre>
                </div>
              </div>

            </div>

          </div>
        )}

        {/* TAB 2: VISUAL CRON & SYSTEMD TIMER */}
        {activeTab === 'cron' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            
            {/* Left: Cron Builder Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f8d210', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaClock />
                  <span>Interactive Cron Schedule Builder</span>
                </div>

                {/* Preset Chips */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '8px' }}>Schedule Presets</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {CRON_PRESETS.map(cp => (
                      <button
                        key={cp.id}
                        onClick={() => handleApplyCronPreset(cp)}
                        style={{
                          background: config.cronPreset === cp.id ? '#f8d210' : 'rgba(15, 23, 42, 0.7)',
                          color: config.cronPreset === cp.id ? '#040d1a' : '#cbd5e1',
                          border: '1px solid rgba(248, 210, 16, 0.3)',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {cp.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 5 Field Inputs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Minute (0-59)</label>
                    <input
                      type="text"
                      value={config.cronMinute}
                      onChange={(e) => updateField('cronMinute', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', textAlign: 'center', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.9rem', fontWeight: 700 }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Hour (0-23)</label>
                    <input
                      type="text"
                      value={config.cronHour}
                      onChange={(e) => updateField('cronHour', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', textAlign: 'center', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.9rem', fontWeight: 700 }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Day of Mo (1-31)</label>
                    <input
                      type="text"
                      value={config.cronDom}
                      onChange={(e) => updateField('cronDom', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', textAlign: 'center', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.9rem', fontWeight: 700 }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Month (1-12)</label>
                    <input
                      type="text"
                      value={config.cronMonth}
                      onChange={(e) => updateField('cronMonth', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', textAlign: 'center', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.9rem', fontWeight: 700 }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>Day of Wk (0-7)</label>
                    <input
                      type="text"
                      value={config.cronDow}
                      onChange={(e) => updateField('cronDow', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#38ef7d', fontFamily: 'monospace', textAlign: 'center', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.9rem', fontWeight: 700 }}
                    />
                  </div>
                </div>

                {/* Plain English Translation Banner */}
                <div style={{ background: 'rgba(248, 210, 16, 0.12)', border: '1px solid rgba(248, 210, 16, 0.3)', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaLightbulb color="#f8d210" size={16} />
                  <div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Human-Readable Schedule</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc' }}>{cronHuman}</div>
                  </div>
                </div>

              </div>

              {/* Next 5 Execution Previews */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2ecc71', fontWeight: 700, fontSize: '0.92rem', marginBottom: '12px' }}>
                  <FaRedo />
                  <span>Projected Next 5 Scheduled Executions</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {nextExecutions.map((execTime, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(15, 23, 42, 0.7)', padding: '8px 12px', borderRadius: '6px', fontSize: '0.8rem' }}>
                      <span style={{ color: '#94a3b8' }}>Trigger #{idx + 1}</span>
                      <strong style={{ fontFamily: 'monospace', color: '#2ecc71' }}>{execTime}</strong>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Right: Modern Systemd Timer Code Output */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#f8fafc' }}>
                      /etc/systemd/system/{config.serviceName}.timer
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Modern Systemd Alternative to Crontab</div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleCopy(generateSystemdTimer(config))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: copiedCode ? '#2ecc71' : '#f8d210',
                        color: '#040d1a',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {copiedCode ? <FaCheck /> : <FaCopy />}
                      <span>{copiedCode ? 'Copied!' : 'Copy Timer'}</span>
                    </button>

                    <button
                      onClick={() => handleDownload(`${config.serviceName}.timer`, generateSystemdTimer(config))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: 'rgba(148, 163, 184, 0.15)',
                        color: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload />
                      <span>Download .timer</span>
                    </button>
                  </div>
                </div>

                <div style={{
                  background: '#0a192f',
                  border: '1px solid rgba(255, 71, 87, 0.25)',
                  borderRadius: '8px',
                  padding: '16px',
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: '520px'
                }}>
                  <pre style={{ margin: 0, fontFamily: "'Courier New', Courier, monospace", fontSize: '0.82rem', color: '#f8d210', lineHeight: '1.45' }}>
                    {generateSystemdTimer(config)}
                  </pre>
                </div>
              </div>

            </div>

          </div>
        )}

        {/* TAB 3: CIS SECURITY HARDENING & AUDIT */}
        {activeTab === 'hardening' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            
            <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(255, 71, 87, 0.25)', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
                <div>
                  <h2 style={{ fontSize: '1.25rem', color: '#f8fafc', margin: '0 0 6px 0', fontWeight: 700 }}>
                    CIS Benchmark Linux Daemon Security Audit
                  </h2>
                  <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                    Evaluates process privilege escalation protections, filesystem isolation, and kernel namespace sandboxing.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>CIS Compliance Grade</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: hardening.badgeColor }}>
                      {hardening.grade}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist of Directives */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {hardening.checks.map((check, idx) => (
                <div
                  key={idx}
                  style={{
                    background: check.passed ? 'rgba(46, 204, 113, 0.08)' : 'rgba(235, 77, 75, 0.08)',
                    border: `1px solid ${check.passed ? 'rgba(46, 204, 113, 0.25)' : 'rgba(235, 77, 75, 0.25)'}`,
                    borderLeft: `5px solid ${check.passed ? '#2ecc71' : '#ff4757'}`,
                    borderRadius: '0 8px 8px 0',
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                >
                  <div style={{ marginTop: '2px' }}>
                    {check.passed ? <FaCheckCircle color="#2ecc71" size={18} /> : <FaTimesCircle color="#ff4757" size={18} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#f8fafc', marginBottom: '2px' }}>
                      {check.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>
                      {check.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* TAB 4: AUTOMATED DEPLOYMENT SHELL SCRIPT */}
        {activeTab === 'deploy' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{
              background: 'rgba(8, 20, 39, 0.7)',
              border: '1px solid rgba(255, 71, 87, 0.25)',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '14px'
            }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', color: '#f8fafc', margin: '0 0 6px 0', fontWeight: 700 }}>
                  Idempotent Bash Production Provisioning Script
                </h2>
                <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                  Provisions dedicated system user, sets up directories, installs service, reloads systemd daemon, and configures logrotate.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleCopy(generateDeploymentBashScript(config))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: copiedCode ? '#2ecc71' : '#ff4757',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {copiedCode ? <FaCheck /> : <FaCopy />}
                  <span>{copiedCode ? 'Copied to Clipboard!' : 'Copy Shell Script'}</span>
                </button>

                <button
                  onClick={() => handleDownload('deploy_service.sh', generateDeploymentBashScript(config))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: 'rgba(148, 163, 184, 0.15)',
                    color: '#f8fafc',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <FaDownload />
                  <span>Download deploy_service.sh</span>
                </button>
              </div>
            </div>

            {/* Code View */}
            <div style={{
              background: '#0a192f',
              border: '1px solid rgba(255, 71, 87, 0.25)',
              borderRadius: '12px',
              padding: '20px',
              overflowX: 'auto',
              maxHeight: '650px',
              overflowY: 'auto'
            }}>
              <pre style={{
                margin: 0,
                fontFamily: "'Courier New', Courier, monospace",
                fontSize: '0.84rem',
                lineHeight: '1.5',
                color: '#38ef7d'
              }}>
                {generateDeploymentBashScript(config)}
              </pre>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default LinuxDevOpsPage;
